import { useMemo } from 'react';

import { PermissionRoles } from '@gearbelt/global';
import { IAccount, Products } from '@site-mate/sitemate-global-shared';
import { useQueries, useQuery } from '@tanstack/react-query';

import {
  DASHPIVOT_CONTROLLER_ROLE,
  IAccountCreatePermissions,
  IAccountGroup,
  resolveAccountCreatePermissions,
} from '@/components/workspace-switcher/workspace-switcher-menu.utils';
import { getAccountQueryOptions, getMyPermissionsQueryOptions } from '@/queries/workspace-switcher-permissions';

interface UseWorkspaceCreatePermissionsParams {
  accounts: IAccountGroup[];
  isSuperUser: boolean;
  enabled?: boolean;
}

export function useWorkspaceCreatePermissions({ accounts, isSuperUser, enabled = true }: UseWorkspaceCreatePermissionsParams) {
  const accountIds = useMemo(() => accounts.map((a) => a.accountId), [accounts]);

  const { data: permissions = [], isLoading: isLoadingPermissions } = useQuery({
    ...getMyPermissionsQueryOptions({ includeRoles: [DASHPIVOT_CONTROLLER_ROLE, PermissionRoles.GearbeltAdmin] }),
    enabled: enabled && !isSuperUser,
  });

  const accountQueries = useQueries({
    queries: accountIds.map((accountId) => ({
      ...getAccountQueryOptions(accountId),
      enabled: enabled && accountIds.length > 0,
    })),
  });

  const isLoadingAccounts = accountQueries.some((q) => q.isLoading);
  const accountData = accountQueries.map((q) => q.data);

  const permissionsByAccount = useMemo(() => {
    const result = new Map<string, IAccountCreatePermissions>();

    for (let i = 0; i < accountIds.length; i++) {
      const accountId = accountIds[i];
      const account = accountData[i];
      const workspaceIds = accounts[i]?.products.flatMap((p) => p.workspaces.map((ws) => ws.id)) ?? [];
      result.set(accountId, resolveAccountCreatePermissions(accountId, account, permissions, isSuperUser, workspaceIds));
    }

    return result;
  }, [accountIds, accounts, accountData, permissions, isSuperUser]);

  const accountsByAccountId = useMemo(() => {
    const result = new Map<string, IAccount | null | undefined>();
    for (let i = 0; i < accountIds.length; i++) {
      result.set(accountIds[i], accountData[i]);
    }
    return result;
  }, [accountIds, accountData]);

  const getProductPermission = useMemo(() => {
    return (accountId: string, product: Products) => {
      return permissionsByAccount.get(accountId)?.[product] ?? { canCreate: false };
    };
  }, [permissionsByAccount]);

  return {
    permissionsByAccount,
    accountsByAccountId,
    getProductPermission,
    isLoading: !isSuperUser && (isLoadingPermissions || isLoadingAccounts),
  };
}
